/**
 * The lines drawn around a model rather than on it.
 *
 * A grid, three axes and a bounding box. All three are line geometry in the
 * model's own coordinate system, built once when the mesh loads and drawn with
 * one unlit program - which is why they are here rather than inside
 * `ModelViewer`: the surface shader is lit, two-sided and textured, and none
 * of that applies to a line.
 *
 * **The grid is not decoration, it is the scale.** Its spacing is a round
 * number in the model's units, chosen from the model's own size, and the
 * viewer states that spacing next to it. On a scale-calibrated model that
 * makes the grid a ruler; on an uncalibrated one the viewer says so instead of
 * printing a number that would be read as millimetres.
 *
 * Colours are fixed rather than themed. The three axes have to stay X-red,
 * Y-green, Z-blue because that convention is older than this product and a
 * reader who knows it would be misled by a palette that reassigned it; the
 * grid is drawn at low contrast against both the light and the dark void, so
 * it reads on either without being read as part of the model.
 */

import type { Framing } from "./viewerFraming";

export interface LineGeometry {
  /** xyz pairs; every two vertices are one segment. */
  positions: Float32Array;
  /** rgb per vertex, matching `positions`. */
  colors: Float32Array;
}

/**
 * A round grid spacing for a model of this size, and how many lines to draw.
 *
 * "Round" means 1, 2 or 5 times a power of ten - the same ladder a chart axis
 * uses, and for the same reason: a grid at 0.0734 units tells a reader
 * nothing, and one at 0.05 tells them how big the thing is.
 */
export function gridSpacing(extent: number): number {
  if (!Number.isFinite(extent) || extent <= 0) return 1;
  // Aim for roughly ten divisions across the model.
  const target = extent / 10;
  const magnitude = 10 ** Math.floor(Math.log10(target));
  const normalised = target / magnitude;
  const step = normalised >= 5 ? 5 : normalised >= 2 ? 2 : 1;
  return step * magnitude;
}

/**
 * A ground plane under the model.
 *
 * Placed at the bottom of the bounding box rather than at the model's origin:
 * a reconstruction's origin is wherever the solver put it, frequently inside
 * the subject, and a grid drawn through the middle of somebody's head is not a
 * ground plane.
 */
export function buildGrid(
  min: readonly [number, number, number],
  max: readonly [number, number, number],
  framing: Framing,
): { geometry: LineGeometry; spacing: number } {
  const extent = Math.max(max[0] - min[0], max[2] - min[2], framing.radius * 2);
  const spacing = gridSpacing(extent);
  // One and a half model widths, so the grid reads as a floor the model stands
  // on rather than as a plate exactly its size.
  const half = Math.ceil((extent * 0.75) / spacing) * spacing;
  const centreX = (min[0] + max[0]) / 2;
  const centreZ = (min[2] + max[2]) / 2;
  const y = min[1];

  const positions: number[] = [];
  const colors: number[] = [];
  const faint: [number, number, number] = [0.42, 0.44, 0.48];
  const strong: [number, number, number] = [0.58, 0.6, 0.65];

  for (let offset = -half; offset <= half + 1e-9; offset += spacing) {
    // Every fifth line brighter, which is what makes a grid countable at a
    // glance instead of a texture.
    const index = Math.round(offset / spacing);
    const tone = index % 5 === 0 ? strong : faint;
    positions.push(centreX + offset, y, centreZ - half, centreX + offset, y, centreZ + half);
    positions.push(centreX - half, y, centreZ + offset, centreX + half, y, centreZ + offset);
    for (let vertex = 0; vertex < 4; vertex += 1) colors.push(tone[0], tone[1], tone[2]);
  }

  return {
    geometry: {
      positions: new Float32Array(positions),
      colors: new Float32Array(colors),
    },
    spacing,
  };
}

/** Three axes at the model's own origin, one grid step long. */
export function buildAxes(length: number): LineGeometry {
  const positions = new Float32Array([
    0, 0, 0, length, 0, 0,
    0, 0, 0, 0, length, 0,
    0, 0, 0, 0, 0, length,
  ]);
  const colors = new Float32Array([
    0.85, 0.25, 0.25, 0.85, 0.25, 0.25,
    0.3, 0.75, 0.35, 0.3, 0.75, 0.35,
    0.3, 0.5, 0.9, 0.3, 0.5, 0.9,
  ]);
  return { positions, colors };
}

/** The twelve edges of an axis-aligned box. */
export function buildBoundingBox(
  min: readonly [number, number, number],
  max: readonly [number, number, number],
): LineGeometry {
  const corner = (index: number): [number, number, number] => [
    index & 1 ? max[0] : min[0],
    index & 2 ? max[1] : min[1],
    index & 4 ? max[2] : min[2],
  ];
  // Pairs of corner indices differing in exactly one bit - which is the
  // definition of an edge of a cube, and is why this needs no hand-written
  // table to get wrong.
  const positions: number[] = [];
  for (let a = 0; a < 8; a += 1) {
    for (const bit of [1, 2, 4]) {
      const b = a ^ bit;
      if (b < a) continue;
      positions.push(...corner(a), ...corner(b));
    }
  }
  const colors = new Float32Array(positions.length).fill(0.55);
  return { positions: new Float32Array(positions), colors };
}

/** The bounding box of a vertex buffer, skipping non-finite coordinates. */
export function boundsOf(
  positions: Float32Array,
): { min: [number, number, number]; max: [number, number, number] } {
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (let index = 0; index + 2 < positions.length; index += 3) {
    const x = Number(positions[index]);
    const y = Number(positions[index + 1]);
    const z = Number(positions[index + 2]);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    if (x < min[0]) min[0] = x;
    if (y < min[1]) min[1] = y;
    if (z < min[2]) min[2] = z;
    if (x > max[0]) max[0] = x;
    if (y > max[1]) max[1] = y;
    if (z > max[2]) max[2] = z;
  }
  if (!Number.isFinite(min[0])) return { min: [0, 0, 0], max: [0, 0, 0] };
  return { min, max };
}
